import { useState, useEffect } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { NEIGHBORHOODS } from '../constants/neighborhoods';
import './UpdateProfile.css';

const BIO_MAX_LENGTH = 160;

const UpdateProfile = ({ currentUser, userProfile, onProfileUpdate, editing, setEditing }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [bio, setBio] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userProfile?.firstName) {
      setFirstName(userProfile.firstName);
    }
    if (userProfile?.lastName) {
      setLastName(userProfile.lastName);
    }
    if (userProfile?.neighborhood) {
      setNeighborhood(userProfile.neighborhood);
    }
    if (userProfile?.bio) {
      setBio(userProfile.bio);
    }
    if (userProfile?.phoneNumber) {
      setPhoneNumber(userProfile.phoneNumber);
    }
  }, [userProfile]);

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setFirstName(userProfile?.firstName || '');
    setLastName(userProfile?.lastName || '');
    setNeighborhood(userProfile?.neighborhood || '');
    setBio(userProfile?.bio || '');
    setPhoneNumber(userProfile?.phoneNumber || '');
    setError(null);
    setEditing(false);
  };

  const handleNeighborhoodChange = (e) => {
    setNeighborhood(e.target.value);
  };

  const handleBioChange = (e) => {
    const value = e.target.value;
    if (value.length <= BIO_MAX_LENGTH) {
      setBio(value);
    }
  };

  const handlePhoneNumberChange = (e) => {
    const input = e.target.value;
    const digits = input.replace(/\D/g, '').slice(0, 10);
    let formatted = '';
    if (digits.length > 0) {
      formatted = digits.slice(0, 3);
    }
    if (digits.length > 3) {
      formatted += '-' + digits.slice(3, 6);
    }
    if (digits.length > 6) {
      formatted += '-' + digits.slice(6, 10);
    }
    setPhoneNumber(formatted);
  };

  const handleSave = async () => {
    setError(null);

    if (!currentUser) {
      setError('You must be signed in to update your profile.');
      return;
    }

    setSaving(true);
    try {
      // Public fields stay on users/{uid}.userProfile; bio/phoneNumber
      // (F-07) live on the private contact subdoc, which the owner can
      // write directly but no one else can read without a confirmed
      // booking (see firestore.rules).
      const userDocRef = doc(db, 'users', currentUser.uid);
      await Promise.all([
        setDoc(userDocRef, {
          userProfile: {
            firstName: firstName || null,
            lastName: lastName || null,
            neighborhood: neighborhood || null,
          },
        }, { merge: true }),
        setDoc(doc(db, 'users', currentUser.uid, 'private', 'contact'), {
          bio: bio || null,
          phoneNumber: phoneNumber || null,
        }, { merge: true }),
      ]);
      if (onProfileUpdate) {
        onProfileUpdate({ ...userProfile, firstName, lastName, neighborhood, bio, phoneNumber });
      }
      setEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(
        err.code === 'permission-denied'
          ? "You don't have permission to save these changes."
          : 'Something went wrong saving your profile. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="update-profile">
      {editing && (
        <>
          <div className="profile-row">
            <span className="profile-label">First Name</span>
            <input
              type="text"
              className="profile-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={saving}
              placeholder="Enter your first name"
            />
          </div>

          <div className="profile-row">
            <span className="profile-label">Last Name</span>
            <input
              type="text"
              className="profile-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={saving}
              placeholder="Enter your last name"
            />
          </div>

          <div className="profile-row">
            <span className="profile-label">Phone Number</span>
            <input
              type="tel"
              className="profile-input"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              disabled={saving}
              placeholder="000-000-0000"
            />
          </div>

          <div className="profile-row">
            <span className="profile-label">Neighborhood</span>
            <select
              className="neighborhood-select"
              value={neighborhood}
              onChange={handleNeighborhoodChange}
              disabled={saving}
            >
              <option value="">Select your neighborhood</option>
              {NEIGHBORHOODS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <div className="profile-row profile-row--bio">
            <div className="bio-header">
              <span className="profile-label">Bio</span>
              <span className="bio-char-count">
                {bio.length}/{BIO_MAX_LENGTH}
              </span>
            </div>
            <textarea
              className="bio-textarea"
              value={bio}
              onChange={handleBioChange}
              disabled={saving}
              placeholder="Tell us a bit about yourself..."
              maxLength={BIO_MAX_LENGTH}
            />
          </div>
        </>
      )}

      {error && <p className="profile-error">{error}</p>}

      {!editing ? (
        <button className="edit-profile-button" onClick={handleEdit}>
          Edit Profile
        </button>
      ) : (
        <div className="profile-button-group">
          <button
            className="update-profile-button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Update Profile'}
          </button>
          <button
            className="cancel-button"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default UpdateProfile;
